import { forwardRef, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/utils/style.util";

const avatarVariants = cva(
  [
    "relative inline-flex shrink-0",
    "font-medium select-none",
  ].join(" "),
  {
    variants: {
      size: {
        xs: "size-6 text-[10px]",
        sm: "size-8 text-xs",
        md: "size-10 text-sm",
        lg: "size-12 text-base",
        xl: "size-16 text-lg",
      },
    },
    defaultVariants: { size: "md" },
  },
);

type AvatarVariantsProps = VariantProps<typeof avatarVariants>;

type AvatarStatus = "online" | "offline" | "busy";

type AvatarProps = {
  name: string
  src?: string
  status?: AvatarStatus
} & HTMLAttributes<HTMLSpanElement> & AvatarVariantsProps;

const buildInitials = (name: string): string => {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const statusColor = {
  online: "bg-emerald-500",
  offline: "bg-gray-400",
  busy: "bg-atelier-danger",
} satisfies Record<AvatarStatus, string>;

const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({
    name, src, status, size, className, ...props
  }, ref) => {
    const hasImage = src !== undefined && src.length > 0;
    return (
      <span
        ref={ref}
        className={cn(avatarVariants({ size }), className)}
        {...props}
      >
        <span
          className={cn(
            "size-full inline-flex items-center justify-center overflow-hidden",
            "rounded-atelier-pill bg-atelier-surface-muted text-atelier-ink-primary",
          )}
        >
          {hasImage
            ? (
              <img
                src={src}
                alt={name}
                className="size-full object-cover"
                loading="lazy"
              />
            )
            : (
              <span role="img" aria-label={name}>
                <span aria-hidden="true">{buildInitials(name)}</span>
              </span>
            )}
        </span>
        {status !== undefined
          ? (
            <span
              data-testid="avatar-status"
              aria-label={`Statut : ${status}`}
              className={cn(
                "absolute bottom-0 right-0 rounded-full ring-2 ring-atelier-surface-raised",
                "size-2.5",
                statusColor[status],
              )}
            />
          )
          : null}
      </span>
    );
  },
);

Avatar.displayName = "Atelier.Avatar";

export { Avatar, buildInitials };
export type { AvatarProps, AvatarStatus };
